import type { ICartSchema } from '@r/types/zod-schemas.ts';
import type { z } from 'zod';
import { Button } from '@ui/button.tsx';
import { Card, CardContent, CardFooter, CardHeader } from '@ui/card.tsx';
import { useStepper } from '@client/pages/cart/cart.tsx';
import { TableBody, TableCell, TableHead, TableHeader, TableRow, Table } from '@ui/table.tsx';
import { mapCartItemToStoreCartLineItem } from '@client/utils/cart-utils.ts';
import pointsTranslation from '@r/translations/points-translation.ts';
import { useTranslation } from 'react-i18next';
import { CheckCircleIcon, CoinsIcon, MailIcon, TruckIcon } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { toast } from 'sonner';
import { useBalance } from '@r/context/balance-context.ts';
import { FormattedHtml } from '@r/components/formatted-html.tsx';

interface CartSummaryProps {
  cart: z.infer<typeof ICartSchema>;
  context: 'cart' | 'shipping';
  onSubmit?: () => void;
  hasInvalidItems?: boolean;
  isCheckingCart?: boolean;
}

const CartSummary = ({
  cart,
  context,
  onSubmit,
  hasInvalidItems = false,
  isCheckingCart = false
}: CartSummaryProps) => {
  const { t } = useTranslation();
  const stepper = useStepper();
  const items = cart?.items.map((item) => mapCartItemToStoreCartLineItem(item /*, cart*/));
  const { balance } = useBalance();
  const instantDeliveryCount = cart.items
    .filter((item) => item.product.isDemat)
    .reduce((sum, item) => sum + item.quantity, 0);
  const standardDeliveryCount = cart.items
    .filter((item) => !item.product.isDemat)
    .reduce((sum, item) => sum + item.quantity, 0);

  const handleNext = () => {
    if (hasInvalidItems) {
      toast.error(t('cart.error.stock.insufficient'));
      return;
    }

    if (balance < (cart.totals.total ?? 0)) {
      toast.error(
        t('cart.summary.error.not.enough.points', {
          amount: pointsTranslation(t, cart.totals.total ?? 0, true)
        })
      );

      return;
    }

    if (context === 'shipping' && onSubmit) {
      onSubmit();
    }

    stepper.next();
  };

  return (
    <div className="flex flex-col gap-y-4">
      <h2 className="text-[2rem] leading-[2.75rem] font-semibold">{t('cart.summary.title')}</h2>

      <Card className="flex flex-col border-none">
        <CardHeader>
          <p className="text-center text-muted-foreground">
            {t('cart.summary.items.count', {
              cartItemsLength: cart.items.length
            })}
          </p>

          <div className="w-full h-px my-4 border-b border-gray-200" />
        </CardHeader>

        <CardContent>
          <Table>
            <TableHeader className="bg-transparent">
              <TableRow className="border-gray-200 border-dashed hover:bg-transparent">
                <TableHead className="align-top w-20 !pl-0 font-medium">
                  {t('cart.summary.table.quantity')}
                </TableHead>

                <TableHead className="font-medium align-top">
                  {t('cart.summary.table.item')}
                </TableHead>

                <TableHead className="w-24 font-medium text-right align-top">
                  {t('cart.summary.table.amount')}
                  <div className="text-[0.7rem]">({t('point.name_other')})</div>
                </TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {items.map((item) => (
                <TableRow
                  key={`${item.product_iri}-${item.added_at}`}
                  className="border-b border-gray-200 border-dashed hover:bg-transparent"
                >
                  <TableCell>{item.quantity}</TableCell>

                  <TableCell>
                    <Link to={item.product_iri} className="hover:underline">
                      <FormattedHtml as="span" text={item.product_title} />
                      &nbsp;
                      {item.dynamic_price_amount &&
                        t('item.dynamic.price', {
                          amount: item.dynamic_price_amount,
                          currency: '€'
                        })}
                    </Link>
                  </TableCell>

                  <TableCell className="text-center">{item.total}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="w-full h-px my-4 border-b border-gray-200" />

          <div className="flex items-center justify-between">
            <p className="text-sm">{t('cart.summary.total.amount')}</p>
            <span className="flex font-semibold gap-x-2">
              {pointsTranslation(t, cart.totals.total, true)}
              <CoinsIcon />
            </span>
          </div>
        </CardContent>

        <CardFooter className="flex-col items-start space-y-10">
          <div className="space-y-5 text-muted-foreground">
            <div
              className={`flex gap-x-2.5 ${
                balance < (cart.totals.total ?? 0) ? 'text-destructive' : ''
              }`}
            >
              <CoinsIcon size={20} />
              <span className="text-sm">
                {balance < (cart.totals.total ?? 0) ? (
                  t('cart.summary.error.insufficient.balance')
                ) : (
                  <>
                    {t('cart.summary.balance.after.order')}
                    {pointsTranslation(t, balance - (cart.totals.total ?? 0), true)}
                  </>
                )}
              </span>
            </div>

            {standardDeliveryCount === 0 && (
              <div className="flex items-center gap-x-2.5">
                <MailIcon size={20} />
                <p className="text-sm">{t('cart.summary.instant.delivery')}</p>
              </div>
            )}

            {instantDeliveryCount === 0 && (
              <div className="flex items-center gap-x-2.5">
                <TruckIcon size={20} />
                <p className="text-sm">{t('shop.product.delivery.standard')}</p>
              </div>
            )}

            {instantDeliveryCount > 0 && standardDeliveryCount > 0 && (
              <>
                <div className="flex items-center gap-x-2.5">
                  <MailIcon size={20} />
                  <p className="text-sm">
                    {t('cart.summary.instant.delivery.count', {
                      itemsCount: instantDeliveryCount
                    })}
                  </p>
                </div>

                <div className="flex items-center gap-x-2.5">
                  <TruckIcon size={20} />
                  <p className="text-sm">
                    {t('cart.summary.standard.delivery.count', {
                      itemsCount: standardDeliveryCount
                    })}
                  </p>
                </div>
              </>
            )}
          </div>

          <Button
            onClick={handleNext}
            rounded="full"
            className="w-full"
            disabled={hasInvalidItems || isCheckingCart}
          >
            {context === 'cart' && (
              <>
                <TruckIcon />
                {t('cart.summary.action.continue.to.shipping')}
              </>
            )}

            {context === 'shipping' && (
              <>
                <CheckCircleIcon />
                {t('cart.summary.action.confirm.coordinates')}
              </>
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default CartSummary;
